import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Copy, 
  Edit, 
  Trash2, 
  FileText,
  Globe,
  Lock
} from 'lucide-react';
import { templatesApi } from '../services/api';
import { Template } from '../types';
import toast from 'react-hot-toast';

const Templates: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { data: templates, isLoading } = useQuery('templates', () =>
    templatesApi.getAll().then(r => r.data)
  );

  const { data: categories } = useQuery('templateCategories', () =>
    templatesApi.getCategories().then(r => r.data)
  );

  const deleteMutation = useMutation(
    (id: string) => templatesApi.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('templates');
        toast.success('Template deleted');
      },
      onError: () => toast.error('Failed to delete template')
    }
  );

  const cloneMutation = useMutation(
    (id: string) => templatesApi.clone(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('templates');
        toast.success('Template cloned');
      },
      onError: () => toast.error('Failed to clone template')
    }
  );

  const filteredTemplates = templates?.filter((t: Template) => {
    const matchesSearch = t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         t.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || t.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
          <p className="text-gray-600">Manage inspection templates and forms</p>
        </div>
        <button
          onClick={() => navigate('/templates/new')}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create Template
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              showFilters ? 'bg-primary-50 border-primary-500 text-primary-700' : 'border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-5 h-5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t flex items-center gap-4">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
            >
              <option value="">All Categories</option>
              {categories?.categories?.map((cat: string) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <button
              onClick={() => {
                setFilterCategory('');
                setSearchQuery('');
              }}
              className="text-gray-600 hover:text-gray-900"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Templates Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredTemplates?.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No templates found</h3>
          <p className="text-gray-600 mt-2">Create your first template to get started</p>
          <button
            onClick={() => navigate('/templates/new')}
            className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
          >
            Create template →
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template: Template) => (
            <div key={template.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {template.isPublic ? (
                      <Globe className="w-4 h-4 text-green-500" title="Public" />
                    ) : (
                      <Lock className="w-4 h-4 text-gray-400" title="Private" />
                    )}
                    <span className="text-xs text-gray-500 uppercase tracking-wide">
                      {template.category || 'Uncategorized'}
                    </span>
                  </div>
                  <div className="relative group">
                    <button className="p-1 rounded-lg hover:bg-gray-100">
                      <MoreVertical className="w-5 h-5 text-gray-400" />
                    </button>
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                      <button
                        onClick={() => navigate(`/templates/${template.id}/edit`)}
                        className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-gray-50"
                      >
                        <Edit className="w-4 h-4" /> Edit
                      </button>
                      <button
                        onClick={() => cloneMutation.mutate(template.id)}
                        className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-gray-50"
                      >
                        <Copy className="w-4 h-4" /> Duplicate
                      </button>
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="flex items-center gap-2 w-full px-4 py-2 text-left text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                  </div>
                </div>

                <Link to={`/templates/${template.id}/edit`}>
                  <h3 className="text-lg font-semibold text-gray-900 mt-3 hover:text-primary-600">
                    {template.title}
                  </h3>
                </Link>

                {template.description && (
                  <p className="text-gray-600 text-sm mt-2 line-clamp-2">{template.description}</p>
                )}

                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-gray-500">
                    {template.questions?.length || 0} questions
                  </span>
                  <button
                    onClick={() => navigate(`/inspections/new?template=${template.id}`)}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Start inspection →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Templates;